// ===== IndexedDB 存储层 =====
// 解决 localStorage 5MB 配额导致的大数据写入崩溃问题。
// 数据量大（商品 1.4MB + 订单 4.8MB），IndexedDB 配额通常为磁盘的 50%+，完全无压力。
// v2：敏感 key（客户/供应商/商品/销售单）支持 AES-256-GCM 透明加密（见 crypto.ts）

const DB_NAME = 'changfa_workbench';
const STORE = 'kv';
const VERSION = 1;

// 存储 key（与 importedData 保持一致的命名，前缀改为 idb_ 区分）
export const KEYS = {
  customers: 'idb_customers',
  suppliers: 'idb_suppliers',
  products: 'idb_products',
  salesOrders: 'idb_sales_orders',
  warehouses: 'idb_warehouses',
  trash: 'idb_trash',
  importedAt: 'idb_imported_at',
  version: 'idb_data_version',
};

// 数据版本号：Excel 数据更新后 +1，触发重新导入
export const DATA_VERSION = 'v4-20260812';

// 敏感数据 key（启用存储加密时自动加密/解密）
import {
  isEncryptionEnabled,
  getEncryptionKey,
  encryptPayload,
  decryptPayload,
  isEncryptedPayload,
  rekeyEncryption,
  registerEncryptionStorageMigrator,
} from './crypto';

const SENSITIVE_KEYS = new Set<string>([
  KEYS.customers,
  KEYS.suppliers,
  KEYS.products,
  KEYS.salesOrders,
]);

export const SENSITIVE_KEY_LIST = [KEYS.customers, KEYS.suppliers, KEYS.products, KEYS.salesOrders];

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB 被其他标签页占用，请关闭其他标签页后刷新'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => reject(tx.error);
  });
}

export async function dbGet<T>(key: string): Promise<T | null> {
  try {
    const v = await withStore<unknown>('readonly', (s) => s.get(key));
    if (v === undefined) return null;
    // 敏感数据且为加密负载 → 解密（未解锁时返回 null，保护数据）
    if (SENSITIVE_KEYS.has(key) && isEncryptedPayload(v)) {
      const keyObj = await getEncryptionKey();
      if (!keyObj) return null;
      const plain = await decryptPayload(v as string, keyObj);
      try {
        return JSON.parse(plain) as T;
      } catch {
        return null;
      }
    }
    return v as T | null;
  } catch {
    return null;
  }
}

// ===== 写入监听（多设备同步引擎挂载点）=====
// 同步引擎通过 onDbWrite 注册回调；每次 dbSet/dbSetPlain/dbDel 成功后收到通知（同步调用）。
// 引擎内部用 applyingRemote 标志忽略「拉取应用」引发的写入，防止回环。
type DbWriteListener = (key: string) => void;
const writeListeners = new Set<DbWriteListener>();

export function onDbWrite(fn: DbWriteListener): () => void {
  writeListeners.add(fn);
  return () => writeListeners.delete(fn);
}

function notifyWrite(key: string): void {
  if (writeListeners.size === 0) return;
  for (const fn of writeListeners) {
    try { fn(key); } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
}

export async function dbSet(key: string, value: unknown): Promise<void> {
  try {
    let toStore = value;
    // 敏感数据 + 加密已启用 → 加密写入
    if (SENSITIVE_KEYS.has(key) && isEncryptionEnabled()) {
      const keyObj = await getEncryptionKey();
      if (keyObj) toStore = await encryptPayload(JSON.stringify(value), keyObj);
    }
    await withStore('readwrite', (s) => s.put(toStore, key));
    notifyWrite(key);
  } catch (e) {
    console.error('IndexedDB 写入失败', key, e);
  }
}

/** 明文写入（绕过加密）——仅内部流程（关闭加密时回写明文）使用 */
export async function dbSetPlain(key: string, value: unknown): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.put(value, key));
    notifyWrite(key);
  } catch (e) {
    console.error('IndexedDB 明文写入失败', key, e);
  }
}

/**
 * 带成功校验的写入：写入失败会抛出异常，调用方可以回滚。
 * 用于回收站恢复等「必须先确认写入成功，才能删除原记录」的场景。
 */
export async function dbSetChecked(key: string, value: unknown): Promise<void> {
  try {
    let toStore = value;
    if (SENSITIVE_KEYS.has(key) && isEncryptionEnabled()) {
      const keyObj = await getEncryptionKey();
      if (keyObj) toStore = await encryptPayload(JSON.stringify(value), keyObj);
    }
    await withStore('readwrite', (s) => s.put(toStore, key));
    notifyWrite(key);
  } catch (e) {
    console.error('IndexedDB 写入失败', key, e);
    throw e;
  }
}
/** 修改加密口令：旧 key 解密 → 新口令重新加密全部敏感数据（强度校验由 crypto.changeEncryptionPassphrase 上游负责） */
export async function reencryptAll(newPass: string): Promise<void> {
  const data = await Promise.all(SENSITIVE_KEY_LIST.map((k) => dbGet(k)));
  await rekeyEncryption(newPass);
  await Promise.all(
    SENSITIVE_KEY_LIST.map((k, i) => (data[i] != null ? dbSet(k, data[i]) : Promise.resolve()))
  );
}

/** 关闭加密：解密全部敏感数据并回写明文（调用方负责在完成后清除加密设置） */
export async function decryptAllToPlain(): Promise<void> {
  const data = await Promise.all(SENSITIVE_KEY_LIST.map((k) => dbGet(k)));
  await Promise.all(
    SENSITIVE_KEY_LIST.map((k, i) => (data[i] != null ? dbSetPlain(k, data[i]) : Promise.resolve()))
  );
}

registerEncryptionStorageMigrator({ reencryptAll, decryptAllToPlain });

export async function dbDel(key: string): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(key));
    notifyWrite(key);
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

export async function dbClear(): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.clear());
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}

// 清理旧的 localStorage 大 key（v3 及以前版本遗留），防止继续占用配额
export function cleanupLegacyLocalStorage() {
  try {
    const legacy = [
      'changfa_import_customers',
      'changfa_import_suppliers',
      'changfa_import_products',
      'changfa_import_sales_orders',
      'changfa_imported_at',
      'changfa_data_version',
    ];
    legacy.forEach((k) => localStorage.removeItem(k));
  } catch { /* Best-effort operation; failure is non-fatal. */ }
}
